import React, { useState, useEffect } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import WhatsappFormulaire from './WhatsappFormulaire';
import LocationVideo from '../../components/LocationVideo/LocationVideo';
import './Home.css';
import { 
  FaTooth, 
  FaUserMd, 
  FaCalendarCheck, 
  FaPhoneAlt, 
  FaMapMarkerAlt, 
  FaEnvelope,
  FaArrowRight,
  FaBars,
  FaTimes,
  FaShieldAlt,
  FaClinicMedical,
  FaSmile,
  FaStar,
  FaCheckCircle,
  FaClock,
  FaAward,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaDoorOpen,
  FaStethoscope,
  FaXRay,
  FaBuilding,
  FaExpand,
  FaHeart,
  FaInfoCircle,
  FaArrowLeft,
  FaVideo
} from 'react-icons/fa';

// Tableau d'images pour le diaporama
const heroSlides = [
  process.env.PUBLIC_URL + '/images/img.jpg',
  process.env.PUBLIC_URL + '/images/img1.jpg',
  process.env.PUBLIC_URL + '/images/img2.jpg'
];

// Ajoutez une quatrième image
const aboutImage = process.env.PUBLIC_URL + '/images/img1.jpg';

// Données pour les salles de la clinique
const clinicRooms = [
  {
    id: 1,
    image: process.env.PUBLIC_URL + '/images/img1.jpg',
    title: 'Salle d\'Attente',
    description: 'Un espace chaleureux et confortable pour vous accueillir dans les meilleures conditions. Notre salle d\'attente est conçue pour vous détendre avant votre consultation.',
    icon: <FaDoorOpen />,
    features: ['Ambiance relaxante', 'Confort optimal', 'Wi-Fi gratuit', 'Magazines spécialisés']
  },
  {
    id: 2,
    image: process.env.PUBLIC_URL + '/images/img2.jpg',
    title: 'Salle de Consultation',
    description: 'Équipée de technologies de pointe, cette salle permet des diagnostics précis et des traitements dans des conditions d\'hygiène et de confort maximales.',
    icon: <FaStethoscope />,
    features: ['Équipement dernier cri', 'Stérilisation avancée', 'Confort patient', 'Éclairage LED médical']
  },
  {
    id: 3,
    image: process.env.PUBLIC_URL + '/images/img3.jpg',
    title: 'Salle de Radiographie',
    description: 'Notre unité de radiologie digitale permet des examens rapides et précis avec une exposition aux rayons X réduite au minimum nécessaire.',
    icon: <FaXRay />,
    features: ['Radiographie digitale', 'Faible radiation', 'Résultats immédiats', 'Précision maximale']
  }
];

// Texte pour chaque slide du diaporama
const slideContent = [
  {
    title: "Votre santé dentaire",
    subtitle: "notre priorité absolue",
    description: "Une approche moderne et personnalisée des soins dentaires, combinant technologie de pointe et confort patient."
  },
  {
    title: "Technologie de pointe",
    subtitle: "pour des soins précis",
    description: "Équipements dernier cri pour des diagnostics et traitements d'une précision exceptionnelle."
  },
  {
    title: "Environnement accueillant",
    subtitle: "pour votre confort",
    description: "Un cadre chaleureux et relaxant pour une expérience dentaire agréable et sans stress."
  }
];

// Données détaillées pour chaque traitement
const treatmentDetails = {
  0: {
    title: "Soins dentaires généraux",
    description: "Des soins complets pour maintenir une excellente santé bucco-dentaire.",
    longDescription: "Nos soins dentaires généraux comprennent des examens complets, des nettoyages professionnels et des détartrages utilisant les dernières technologies. Nous utilisons des instruments ultrasoniques pour un nettoyage en profondeur sans inconfort.",
    benefits: [
      "Prévention des caries et maladies gingivales",
      "Détection précoce des problèmes dentaires",
      "Élimination du tartre et des plaques",
      "Conseils personnalisés d'hygiène buccale"
    ],
    duration: "30-60 minutes",
    priceRange: "À partir de 300 MAD",
    image: process.env.PUBLIC_URL + '/images/treatment1.jpg',
    videoUrl: "https://www.youtube.com/embed/11jmcj3xTLo" // URL YouTube embed
  },
  1: {
    title: "Blanchiment dentaire",
    description: "Un sourire plus blanc et éclatant grâce à notre technologie LED avancée.",
    longDescription: "Notre traitement de blanchiment dentaire utilise la technologie LED la plus récente pour des résultats visibles dès la première séance. Le gel blanchissant professionnel est appliqué sous supervision médicale pour garantir sécurité et efficacité.",
    benefits: [
      "Résultats visibles en une séance",
      "Technologie LED sans chaleur",
      "Gel professionnel à haute concentration",
      "Sensibilité dentaire minimale"
    ],
    duration: "1 heure",
    priceRange: "À partir de 1500 MAD",
    image: process.env.PUBLIC_URL + '/images/treatment2.jpg',
    videoUrl: "https://www.youtube.com/embed/skeALe88OCk" // NOUVELLE VIDÉO BLANCHIMENT
  },
  2: {
    title: "Implantologie",
    description: "Remplacer des dents manquantes avec des implants de haute qualité.",
    longDescription: "Nos implants dentaires sont fabriqués avec des matériaux biocompatibles de première qualité. Le processus comprend une planification 3D numérique pour une précision optimale et un placement parfait.",
    benefits: [
      "Garantie de 10 ans sur les implants",
      "Scanner 3D pour une planification précise",
      "Matériaux biocompatibles titane grade 5",
      "Suivi post-opératoire complet"
    ],
    duration: "2-3 heures (par séance)",
    priceRange: "À partir de 8000 MAD",
    image: process.env.PUBLIC_URL + '/images/treatment3.jpg',
    videoUrl: "https://www.youtube.com/embed/I6CM5uloxZc?start=65" // VIDÉO IMPLANTOLOGIE AVEC TIMESTAMP
  },
  3: {
    title: "Orthodontie",
    description: "Corriger l'alignement dentaire avec des solutions modernes et discrètes.",
    longDescription: "Nous proposons différentes options orthodontiques incluant les aligneurs invisibles et les appareils dentaires modernes. Chaque traitement est personnalisé selon vos besoins spécifiques.",
    benefits: [
      "Aligneurs invisibles disponibles",
      "Traitement accéléré possible",
      "Suivi numérique régulier",
      "Confort optimisé"
    ],
    duration: "12-24 mois",
    priceRange: "À partir de 12000 MAD",
    image: process.env.PUBLIC_URL + '/images/treatment4.jpg',
    videoUrl: "https://www.youtube.com/embed/IyVrILWJVp8" // NOUVELLE VIDÉO ORTHODONTIE
  },
  4: {
    title: "Endodontie",
    description: "Traitements de canal précis avec microscope opératoire.",
    longDescription: "L'endodontie moderne utilise des microscopes opératoires pour une précision extrême dans les traitements de canal. Cela permet de préserver la dent naturelle tout en éliminant l'infection.",
    benefits: [
      "Microscope opératoire haute précision",
      "Douleur minimale pendant le traitement",
      "Taux de succès élevé (95%)",
      "Préservation de la dent naturelle"
    ],
    duration: "1-2 heures",
    priceRange: "À partir de 2000 MAD",
    image: process.env.PUBLIC_URL + '/images/treatment5.jpg',
    videoUrl: "https://www.youtube.com/embed/V59M3Ezml3Q"
  },
  5: {
    title: "Urgences dentaires",
    description: "Prise en charge rapide et efficace des urgences 7j/7.",
    longDescription: "Notre service d'urgence dentaire est disponible 24h/24 pour traiter les douleurs aiguës, les traumatismes dentaires et les infections. Une équipe dédiée est toujours prête à intervenir.",
    benefits: [
      "Disponible 24h/24, 7j/7",
      "Traitement immédiat de la douleur",
      "Équipement d'urgence complet",
      "Suivi post-urgence assuré"
    ],
    duration: "Variable selon l'urgence",
    priceRange: "Selon le traitement",
    image: process.env.PUBLIC_URL + '/images/treatment6.jpg',
    videoUrl: "https://www.youtube.com/embed/M08npQQFiMY"
  }
};

// Durée en millisecondes (5000ms = 5 secondes)
const SLIDE_DURATION = 8000;

// URL Google Maps pour l'adresse de la clinique
const GOOGLE_MAPS_URL = "https://www.google.com/maps/place/Serenity+Dental+Clinic/@35.7678334,-5.8042277,925m/data=!3m1!1e3!4m6!3m5!1s0xd0b810d32d5d7c7:0x3e507fa6b27c86b3!8m2!3d35.7678359!4d-5.8043202!16s%2Fg%2F11z1rm6jyw!5m1!1e2!18m1!1e1?entry=ttu&g_ep=EgoyMDI2MDMyMy4xIKXMDSoASAFQAw%3D%3D";

const Home = () => {
  const [activeSection, setActiveSection] = useState('accueil');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentRoom, setCurrentRoom] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isRoomPlaying, setIsRoomPlaying] = useState(true);
  const [progress, setProgress] = useState(100);
  const [roomProgress, setRoomProgress] = useState(100);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(0);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState(false);
  const [videoErrors, setVideoErrors] = useState({}); // Gestion des erreurs vidéo
  const [loadedImages, setLoadedImages] = useState(new Set()); // Suivi des images chargées
  const controls = useAnimation();

  // Diaporama automatique avec barre de progression - 5 SECONDES
  useEffect(() => {
    let interval;
    let startTime;
    
    if (isPlaying) {
      startTime = Date.now();
      setProgress(100);
      
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, SLIDE_DURATION - elapsed);
        setProgress((remaining / SLIDE_DURATION) * 100);
        
        if (remaining <= 0) {
          setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
          startTime = Date.now();
          setProgress(100);
        }
      }, 50); // Mise à jour toutes les 50ms pour une animation fluide
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPlaying, currentSlide]);

  // Diaporama automatique pour les salles
  useEffect(() => {
    let interval;
    let startTime;
    
    if (isRoomPlaying) {
      startTime = Date.now();
      setRoomProgress(100);
      
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, SLIDE_DURATION - elapsed);
        setRoomProgress((remaining / SLIDE_DURATION) * 100);
        
        if (remaining <= 0) {
          setCurrentRoom((prev) => (prev + 1) % clinicRooms.length);
          startTime = Date.now();
          setRoomProgress(100);
        }
      }, 50);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRoomPlaying, currentRoom]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  // Fonction pour appeler le numéro de téléphone
  const handleCallClick = () => {
    const phoneNumber = '+212667271927';
    // Utiliser tel: pour ouvrir l'application téléphone
    window.location.href = `tel:${phoneNumber}`;
  };

  // Gestionnaires pour les liens sociaux
  const handleSocialClick = (platform) => {
  if (platform === 'instagram') {
    window.open('https://www.instagram.com/serenity_dental_clinic_/?igsh=MTV5MW9oaXF5dXI1bw%3D%3D#', '_blank');
  } else if (platform === 'facebook') {
    // Ajoutez votre URL Facebook ici si vous en avez une
    // window.open('votre-url-facebook', '_blank');
    console.log('Facebook - URL à définir');
  } else if (platform === 'twitter') {
    // Ajoutez votre URL Twitter ici si vous en avez un
    // window.open('votre-url-twitter', '_blank');
    console.log('Twitter - URL à définir');
  }
};

  const handleLegalClick = (page) => {
    console.log(`Ouvrir ${page}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['accueil', 'a-propos', 'salles', 'traitements', 'localisation', 'contact'];
      const scrollPosition = window.scrollY + 100;
      
      setScrolled(window.scrollY > 50);

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (scrolled) {
      controls.start({ y: 0, opacity: 1 });
    }
  }, [scrolled, controls]);

  // Navigation du diaporama principal
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), SLIDE_DURATION + 2000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), SLIDE_DURATION + 2000);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), SLIDE_DURATION + 2000);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  // Navigation du diaporama des salles
  const nextRoom = () => {
    setCurrentRoom((prev) => (prev + 1) % clinicRooms.length);
    setIsRoomPlaying(false);
    setTimeout(() => setIsRoomPlaying(true), SLIDE_DURATION + 2000);
  };

  const prevRoom = () => {
    setCurrentRoom((prev) => (prev - 1 + clinicRooms.length) % clinicRooms.length);
    setIsRoomPlaying(false);
    setTimeout(() => setIsRoomPlaying(true), SLIDE_DURATION + 2000);
  };

  const goToRoom = (index) => {
    setCurrentRoom(index);
    setIsRoomPlaying(false);
    setTimeout(() => setIsRoomPlaying(true), SLIDE_DURATION + 2000);
  };

  const toggleRoomPlay = () => {
    setIsRoomPlaying(!isRoomPlaying);
  };

  // Modal pour afficher l'image en grand
  const openRoomModal = (index) => {
    setSelectedRoom(index);
    setIsModalOpen(true);
  };

  const closeRoomModal = () => {
    setIsModalOpen(false);
  };

  // Fonction pour ouvrir le modal de traitement
  const openTreatmentModal = (index) => {
    setSelectedTreatment(index);
    setIsTreatmentModalOpen(true);
  };

  // Gestion des erreurs de chargement vidéo
  const handleVideoError = (treatmentIndex) => {
    setVideoErrors(prev => ({ ...prev, [treatmentIndex]: true }));
  };

  // Gestion du chargement des images
  const handleImageLoad = (imageSrc) => {
    setLoadedImages(prev => new Set([...prev, imageSrc]));
  };

  // Vérifier si une vidéo est valide (pas d'URL exemple)
  const isValidVideoUrl = (url) => {
    return url && !url.includes('exemple-') && url.includes('youtube.com');
  };

  const containerVariants = { 
    hidden: { opacity: 0 }, 
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.2, 
        delayChildren: 0.1 
      } 
    } 
  };

  const itemVariants = { 
    hidden: { opacity: 0, y: 30 }, 
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6, 
        ease: "easeOut" 
      } 
    } 
  };

  const navItems = [
    { id: 'accueil', label: 'Accueil', icon: <FaClinicMedical /> },
    { id: 'a-propos', label: 'À propos', icon: <FaUserMd /> },
    { id: 'salles', label: 'Nos Salles', icon: <FaBuilding /> },
    { id: 'traitements', label: 'Traitements', icon: <FaTooth /> },
    { id: 'localisation', label: 'Localisation', icon: <FaMapMarkerAlt /> },
    { id: 'contact', label: 'Contact', icon: <FaPhoneAlt /> }
  ];

  const services = [
    { 
      icon: <FaTooth />, 
      title: 'Soins dentaires généraux', 
      desc: 'Examens complets, nettoyages professionnels et détartrages.',
      features: ['Examen numérique', 'Détartrage ultrasonique', 'Prévention caries']
    },
    { 
      icon: <FaSmile />, 
      title: 'Blanchiment dentaire', 
      desc: 'Technologie LED pour un sourire plus blanc et éclatant.',
      features: ['Technologie LED', 'Résultats visibles', 'Sans sensibilité']
    },
    { 
      icon: <FaShieldAlt />, 
      title: 'Implantologie', 
      desc: 'Implants dentaires avec garantie et suivi personnalisé.',
      features: ['Garantie 10 ans', 'Scanner 3D', 'Suivi personnalisé']
    },
    { 
      icon: <FaTooth />, 
      title: 'Orthodontie', 
      desc: 'Traitements invisibles et appareils dentaires modernes.',
      features: ['Aligneurs invisibles', 'Traitement rapide', 'Confort optimal']
    },
    { 
      icon: <FaClinicMedical />, 
      title: 'Endodontie', 
      desc: 'Traitements de canal avec microscope opératoire.',
      features: ['Microscope opératoire', 'Précision maximale', 'Douleur minimale']
    },
    { 
      icon: <FaCalendarCheck />, 
      title: 'Urgences dentaires', 
      desc: 'Prise en charge rapide 7j/7 pour vos urgences.',
      features: ['Disponible 24/7', 'Traitement immédiat', 'Douleurs aiguës']
    }
  ];

  // Témoignages (réservé pour utilisation future)
  // const testimonials = [
  //   {
  //     name: 'Sarah M.',
  //     role: 'Patiente depuis 3 ans',
  //     content: 'Excellente clinique ! Le Dr. Assuik est très professionnel et à l\'écoute. Mes traitements se sont parfaitement déroulés.',
  //     rating: 5,
  //     date: '15 Nov 2024'
  //   },
  //   {
  //     name: 'Karim B.',
  //     role: 'Implant dentaire',
  //     content: 'J\'ai reçu un implant il y a 6 mois. Le résultat est naturel et je n\'ai ressenti aucune douleur pendant le traitement.',
  //     rating: 5,
  //     date: '02 Nov 2024'
  //   },
  //   {
  //     name: 'Fatima E.',
  //     role: 'Blanchiment dentaire',
  //     content: 'Service impeccable. Mon sourire est transformé ! L\'équipe est chaleureuse et très professionnelle.',
  //     rating: 5,
  //     date: '28 Oct 2024'
  //   }
  // ];

  return (
    <motion.div 
      className="home-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Navigation */}
      <motion.nav 
        className={`home-navbar ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="navbar-container">
          <motion.div 
            className="navbar-brand"
            whileHover={{ scale: 1.05 }}
          >
            <img 
              src={process.env.PUBLIC_URL + '/images/logo.png'} 
              alt="Logo Serenity Dental Clinic" 
              className="brand-logo"
              loading="eager"
              width="60"
              height="60"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="brand-text">
              <h2>SERENITY DENTAL CLINIC</h2>
            </div>
          </motion.div>

          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className={`navbar-links ${mobileMenuOpen ? 'open' : ''}`}>
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => scrollToSection(item.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`Aller à la section ${item.label}`}
              >
                <span className="nav-link-icon">{item.icon}</span>
                <span className="nav-link-text">{item.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.nav>

      {/* Hero Section avec Diaporama */}
      <motion.section 
        id="accueil" 
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="hero-background"
            style={{
              backgroundImage: `url(${heroSlides[currentSlide]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeInOut" }}
          >
            <div className="hero-overlay"></div>
          </motion.div>
        </AnimatePresence>
        
        {/* Indicateurs de diaporama */}
        <div className="slider-dots" role="tablist" aria-label="Navigation du diaporama">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Aller à l'image ${index + 1}`}
              aria-selected={index === currentSlide}
              role="tab"
              type="button"
            />
          ))}
        </div>
        
        {/* Compteur de slides */}
        <div className="slider-counter">
          {currentSlide + 1} / {heroSlides.length}
        </div>
        
        <div className="hero-container">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentSlide}
              className="hero-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              <motion.div 
                className="hero-badge"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: "spring" }}
              >
                <FaAward /> Excellence Dentaire
              </motion.div>
              
              <motion.h1 
                className="hero-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                {slideContent[currentSlide].title}<br />
                <span className="highlight">{slideContent[currentSlide].subtitle}</span>
              </motion.h1>
              
              <motion.p 
                className="hero-description"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
              >
                {slideContent[currentSlide].description}
              </motion.p>
              
              <motion.div 
                className="hero-buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              >
                <motion.button 
                  className="btn-primary"
                  onClick={() => scrollToSection('contact')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaCalendarCheck /> Prendre rendez-vous
                  <FaArrowRight className="btn-icon" />
                </motion.button>
                
                <motion.button 
                  className="btn-secondary"
                  onClick={handleCallClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  aria-label="Appeler le numéro +212 667 271 927"
                >
                  <FaPhoneAlt /> Appeler maintenant
                </motion.button>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
          >
            {[
              { value: '200+', label: 'Patients satisfaits', icon: <FaSmile /> },
              { value: '100%', label: 'Matériel certifié', icon: <FaShieldAlt /> },
              { value: '24/7', label: 'Urgences dentaires', icon: <FaClock /> }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 + index * 0.1, duration: 0.8 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* À propos */}
      <motion.section 
        id="a-propos" 
        className="about-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <div className="container">
          <div className="section-header">
            <motion.h2 className="section-title centered-title" variants={itemVariants}>
              <FaUserMd /> À propos de nous
            </motion.h2>
          </div>

          <div className="about-grid">
            <motion.div 
              className="about-content"
              variants={itemVariants}
            >
              <div className="about-header">
                <h3>Bienvenue à SERENITY DENTAL CLINIC</h3>
                <p className="about-subtitle">
                  Votre partenaire pour une santé dentaire optimale
                </p>
              </div>
              
              <div className="about-text">
                <p>
                  Notre clinique a été fondée sur des principes d'excellence, d'innovation 
                  et de compassion. Chaque patient bénéficie d'une approche personnalisée 
                  et de soins adaptés à ses besoins spécifiques.
                </p>
                <p>
                  Nous investissons continuellement dans les dernières technologies 
                  dentaires pour garantir des traitements efficaces, rapides et confortables.
                </p>
              </div>
              
              <div className="about-features">
                {[
                  { icon: <FaCheckCircle />, text: 'Consultation personnalisée et détaillée' },
                  { icon: <FaCheckCircle />, text: 'Équipements de dernière génération' },
                  { icon: <FaCheckCircle />, text: 'Urgences traitées 7 jours sur 7' },
                  { icon: <FaCheckCircle />, text: 'Suivi post-opératoire complet' },
                  { icon: <FaCheckCircle />, text: 'Ambiance chaleureuse et relaxante' },
                  { icon: <FaCheckCircle />, text: 'Transparence des tarifs' }
                ].map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="feature-item"
                    whileHover={{ x: 5 }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="feature-icon">{feature.icon}</div>
                    <span>{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="about-image-section"
              variants={itemVariants}
            >
              <div className="image-wrapper">
                <div 
                  className="main-image"
                  style={{
                    backgroundImage: `url(${aboutImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                  role="img"
                  aria-label="Image de la clinique dentaire"
                >
                  <div className="image-overlay"></div>
                  <img 
                    src={aboutImage} 
                    alt="Clinique dentaire Serenity" 
                    loading="lazy"
                    style={{ display: 'none' }}
                    onLoad={() => handleImageLoad(aboutImage)}
                  />
                </div>
                
                <div className="image-stats">
                  <div className="image-stat">
                    <h4>98%</h4>
                    <p>Patients satisfaits</p>
                  </div>
                  <div className="image-stat">
                    
                  </div>


              
                  <div className="image-stat">
                    <h4>200+</h4>
                    <p>Sourires transformés</p>
                  </div>
                </div>

                <div className="doctor-card">
                  <div className="doctor-avatar">
                    <FaUserMd />
                  </div>
                  <div className="doctor-info">
                    <h4>Dr.Asuik</h4>
                    <p>Chirurgien-Dentiste Diplômé</p>
                    <div className="doctor-specialties">
                    
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Services */}
      <motion.section 
        id="traitements" 
        className="services-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="container">
          <div className="section-header">
            <motion.h2 className="section-title centered-title" variants={itemVariants}>
              <FaTooth /> Nos traitements
            </motion.h2>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="service-card"
                variants={itemVariants}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ 
                  y: -10,
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)'
                }}
              >
                <div className="service-header">
                  <div className="service-icon-wrapper">
                    {service.icon}
                  </div>
                  <h3>{service.title}</h3>
                </div>
                
                <p className="service-description">{service.desc}</p>
                
                <div className="service-features">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="service-feature">
                      <FaCheckCircle />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button 
                  className="service-link"
                  onClick={() => openTreatmentModal(index)}
                >
                  <span>Découvrir ce traitement</span>
                  <FaArrowRight />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* NOUVELLE SECTION: Salles de la Clinique */}
      <motion.section 
        id="salles" 
        className="rooms-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <div className="container">
          <div className="section-header">
            <motion.h2 className="section-title centered-title" variants={itemVariants}>
              <FaBuilding /> Nos Salles
            </motion.h2>
          </div>

          <div className="rooms-slider-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentRoom}
                className="room-slide"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
              >
                <div className="room-slide-content">
                  <div className="room-image-container">
                    <div 
                      className="room-image"
                      style={{
                        backgroundImage: `url(${clinicRooms[currentRoom].image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                      onClick={() => openRoomModal(currentRoom)}
                      role="img"
                      aria-label={`Image de ${clinicRooms[currentRoom].title}`}
                    >
                      <div className="room-image-overlay">
                        <button 
                          className="expand-btn" 
                          onClick={() => openRoomModal(currentRoom)}
                          aria-label={`Agrandir l'image de ${clinicRooms[currentRoom].title}`}
                          type="button"
                        >
                          <FaExpand /> Agrandir
                        </button>
                      </div>
                      <img 
                        src={clinicRooms[currentRoom].image} 
                        alt={clinicRooms[currentRoom].title}
                        loading="lazy"
                        style={{ display: 'none' }}
                        onLoad={() => handleImageLoad(clinicRooms[currentRoom].image)}
                      />
                    </div>
                    
                    <div className="room-progress-container">
                      <div 
                        className="room-progress-bar"
                        style={{ width: `${roomProgress}%` }}
                      />
                    </div>
                  </div>

                  <div className="room-info">
                    <motion.div 
                      className="room-header"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="room-icon-wrapper">
                        {clinicRooms[currentRoom].icon}
                      </div>
                      <div>
                        <h3 className="room-title">{clinicRooms[currentRoom].title}</h3>
                        <div className="room-slide-counter">
                          {currentRoom + 1} / {clinicRooms.length}
                        </div>
                      </div>
                    </motion.div>

                    <motion.p 
                      className="room-description"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {clinicRooms[currentRoom].description}
                    </motion.p>

                    <motion.div 
                      className="room-features"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <h4>Caractéristiques :</h4>
                      <div className="features-grid">
                        {clinicRooms[currentRoom].features.map((feature, index) => (
                          <div key={index} className="feature-tag">
                            <FaCheckCircle />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    <motion.div 
                      className="room-controls"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.9 }}
                    >
                      <div className="room-dots">
                        {clinicRooms.map((_, index) => (
                          <button
                            key={index}
                            className={`room-dot ${index === currentRoom ? 'active' : ''}`}
                            onClick={() => goToRoom(index)}
                            aria-label={`Aller à la salle ${index + 1}`}
                          />
                        ))}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.div 
            className="rooms-thumbnails"
            variants={itemVariants}
          >
            {clinicRooms.map((room, index) => (
              <motion.div
                key={room.id}
                className={`room-thumbnail ${index === currentRoom ? 'active' : ''}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => goToRoom(index)}
              >
                <div 
                  className="thumbnail-image"
                  style={{
                    backgroundImage: `url(${room.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="thumbnail-overlay">
                    <div className="thumbnail-icon">{room.icon}</div>
                    <span className="thumbnail-title">{room.title}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Vidéo de Localisation */}
      <LocationVideo />

      {/* Horaires */}
      <motion.section 
        className="schedule-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="container">
          <div className="schedule-card">
            <div className="schedule-overlay"></div>
            
            <motion.div 
              className="schedule-header"
              variants={itemVariants}
            >
              <FaCalendarCheck className="schedule-icon" />
              <h2>Horaires d'ouverture</h2>
              <p>Prenez rendez-vous selon votre disponibilité</p>
            </motion.div>

            <div className="schedule-grid">
              {[
                { 
                  day: 'Lundi - Vendredi', 
                  time: '10:00 - 19:00',
                  available: true,
                  icon: <FaClock />
                },
                { 
                  day: 'Samedi', 
                  time: '10:00 - 14:00',
                  available: true,
                  icon: <FaClock />
                },
                { 
                  day: 'Dimanche', 
                  time: 'Urgences uniquement', 
                  available: false,
                  icon: <FaPhoneAlt />
                }
              ].map((schedule, index) => (
                <motion.div 
                  key={index}
                  className="schedule-item"
                  variants={itemVariants}
                  custom={index}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="schedule-icon-wrapper">
                    {schedule.icon}
                  </div>
                  <h3>{schedule.day}</h3>
                  <p className={`schedule-time ${!schedule.available ? 'unavailable' : ''}`}>
                    {schedule.time}
                  </p>
                  <span className={`schedule-status ${schedule.available ? 'available' : 'limited'}`}>
                    {schedule.available ? '✅ Disponible' : '📱 Sur rendez-vous'}
                  </span>
                </motion.div>
              ))}
            </div>

            <motion.div 
              className="schedule-cta"
              variants={itemVariants}
            >
              <button className="btn-primary large" onClick={() => scrollToSection('contact')}>
                <FaCalendarCheck /> Réserver en ligne
              </button>
              <p className="schedule-note">
                Réservation par téléphone : +212 667-271927 | Fixe : +212 539-352053
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Contact */}
      <motion.section 
        id="contact" 
        className="contact-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <div className="container">
          <div className="section-header">
            <motion.h2 className="section-title centered-title" variants={itemVariants}>
              <FaPhoneAlt /> Contact
            </motion.h2>
          </div>

          <div className="contact-grid">
            <motion.div 
              className="contact-info"
              variants={itemVariants}
            >
              <h3>Nos coordonnées</h3>
              
              <div className="contact-items">
                {[
                  { 
                    icon: <FaMapMarkerAlt />, 
                    title: 'Adresse', 
                    content: 'Riad Tetouan, Rue Marrakech, Résidence Najd, 4ème étage, Appartement 36, Tanger.',
                    action: 'Voir sur la carte'
                  },
                  { 
                    icon: <FaPhoneAlt />, 
                    title: 'Téléphone', 
                    content: '+212 667-271927',
                    action: 'Appeler maintenant'
                  },
                  { 
                    icon: <FaEnvelope />, 
                    title: 'Email', 
                    content: 'serenitydental.clinic@outlook.com',
                    action: 'Envoyer un email'
                  }
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="contact-item"
                    whileHover={{ x: 5 }}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="contact-icon-wrapper">
                      {item.icon}
                    </div>
                    <div className="contact-content">
                      <h4>{item.title}</h4>
                      <p>{item.content}</p>
                      <button 
                        className="contact-action"
                        onClick={() => {
                          if (item.title === 'Téléphone') {
                            // Utiliser tel: pour ouvrir l'application téléphone
                            window.location.href = `tel:${item.content.replace(/\s|-/g, '')}`;
                          } else if (item.title === 'Email') {
                            window.open(`mailto:${item.content}`, '_blank');
                          } else if (item.title === 'Adresse') {
                            window.open(GOOGLE_MAPS_URL, '_blank');
                          }
                        }}
                        type="button"
                        aria-label={item.action}
                      >
                        {item.action} <FaArrowRight />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="social-links">
                <h4>Suivez-nous</h4>
                <div className="social-icons">
                  <button 
                    className="social-icon"
                    onClick={() => handleSocialClick('facebook')}
                    aria-label="Facebook"
                  >
                    <FaFacebook />
                  </button>
                  <button 
                    className="social-icon"
                    onClick={() => handleSocialClick('instagram')}
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </button>
                  <button 
                    className="social-icon"
                    onClick={() => handleSocialClick('twitter')}
                    aria-label="Twitter"
                  >
                    <FaTwitter />
                  </button>
                </div>
              </div>

              <div className="contact-cta">
                <h4>📞 Besoin d'une urgence dentaire ?</h4>
                <p>Nous sommes disponibles 24h/24 pour les urgences</p>
                <button 
                  className="btn-secondary"
                  onClick={() => window.location.href = 'tel:+212667271927'}
                  type="button"
                  aria-label="Appeler pour une urgence dentaire"
                >
                  <FaPhoneAlt /> Urgence dentaire
                </button>
              </div>
            </motion.div>

            <motion.div 
              className="contact-form-wrapper"
              variants={itemVariants}
            >
              <WhatsappFormulaire />
              <div className="form-footer">
                <p>
                  <FaClock /> Réponse garantie sous 24h
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* CTA Banner */}
      <motion.section 
        className="cta-banner"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="container">
          <div className="cta-content">
            <h2>Prêt pour un sourire éclatant ?</h2>
            
            <div className="cta-buttons">
              <button 
                className="btn-primary"
                onClick={() => scrollToSection('contact')}
              >
                <FaCalendarCheck /> Prendre rendez-vous
              </button>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <motion.footer 
        className="home-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <FaTooth className="footer-logo" />
              <div className="footer-brand-info">
                <h3>SERENITY DENTAL CLINIC</h3>
                
                <p className="footer-description">
                  Votre santé dentaire entre de bonnes mains. 
                  Excellence, innovation et compassion.
                </p>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Navigation</h4>
                {navItems.map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="footer-link"
                  >
                    {item.icon} {item.label}
                  </button>
                ))}
              </div>

              <div className="footer-column">
                <h4>Services</h4>
                {services.slice(0, 5).map((service, index) => (
                  <button 
                    key={index} 
                    className="footer-link"
                    onClick={() => scrollToSection('traitements')}
                  >
                    <FaArrowRight /> {service.title}
                  </button>
                ))}
              </div>

              <div className="footer-column">
                <h4>Contact rapide</h4>
                <p className="footer-contact">
                  <FaPhoneAlt /> +212 667-271927
                </p>
                <p className="footer-contact">
                  <FaEnvelope /> serenitydental.clinic@outlook.com
                </p>
                <p className="footer-contact">
                  <FaMapMarkerAlt /> Tanger, Maroc
                </p>
                <div className="footer-social">
                  <button 
                    className="footer-social-btn"
                    onClick={() => handleSocialClick('facebook')}
                    aria-label="Facebook"
                  >
                    <FaFacebook />
                  </button>
                  <button 
                    className="footer-social-btn"
                    onClick={() => handleSocialClick('instagram')}
                    aria-label="Instagram"
                  >
                    <FaInstagram />
                  </button>
                  <button 
                    className="footer-social-btn"
                    onClick={() => handleSocialClick('twitter')}
                    aria-label="Twitter"
                  >
                    <FaTwitter />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p>&copy; {new Date().getFullYear()} SERENITY DENTAL CLINIC. Tous droits réservés.</p>
              <div className="footer-legal">
                <button 
                  onClick={() => handleLegalClick('mentions-legales')}
                  className="footer-legal-link"
                >
                  Mentions légales
                </button>
                <button 
                  onClick={() => handleLegalClick('politique-de-confidentialite')}
                  className="footer-legal-link"
                >
                  Politique de confidentialité
                </button>
                <button 
                  onClick={() => handleLegalClick('conditions-utilisation')}
                  className="footer-legal-link"
                >
                  Conditions d'utilisation
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* Modal pour afficher l'image en grand */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div 
            className="room-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeRoomModal}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close"
                onClick={closeRoomModal}
                aria-label="Fermer le modal"
                type="button"
              >
                <FaTimes aria-hidden="true" />
                <span className="sr-only">Fermer</span>
              </button>
              
              <div 
                className="modal-image"
                style={{
                  backgroundImage: `url(${clinicRooms[selectedRoom].image})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
                role="img"
                aria-label={`Image agrandie de ${clinicRooms[selectedRoom].title}`}
              >
                <img 
                  src={clinicRooms[selectedRoom].image} 
                  alt={clinicRooms[selectedRoom].title}
                  loading="lazy"
                  style={{ display: 'none' }}
                  onLoad={() => handleImageLoad(clinicRooms[selectedRoom].image)}
                />
              </div>
              
              <div className="modal-info">
                <div className="modal-header">
                  <div className="modal-icon-wrapper">
                    {clinicRooms[selectedRoom].icon}
                  </div>
                  <h3>{clinicRooms[selectedRoom].title}</h3>
                </div>
                <p className="modal-description">
                  {clinicRooms[selectedRoom].description}
                </p>
                <div className="modal-features">
                  <h4>Équipements & caractéristiques :</h4>
                  <div className="features-list">
                    {clinicRooms[selectedRoom].features.map((feature, index) => (
                      <div key={index} className="modal-feature">
                        <FaHeart />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal des Traitements Détaillés */}
<AnimatePresence>
  {isTreatmentModalOpen && selectedTreatment !== null && (
    <motion.div 
      className="treatment-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => setIsTreatmentModalOpen(false)}
    >
      <motion.div 
        className="treatment-modal"
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          className="treatment-modal-close"
          onClick={() => setIsTreatmentModalOpen(false)}
          aria-label="Fermer le modal de traitement"
          type="button"
        >
          <FaTimes aria-hidden="true" />
          <span className="sr-only">Fermer</span>
        </button>
        
        <div className="treatment-modal-content">
          <motion.div 
            className="treatment-media-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="treatment-image-container">
              <div 
                className="treatment-modal-image"
                style={{
                  backgroundImage: `url(${treatmentDetails[selectedTreatment].image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                role="img"
                aria-label={`Image du traitement ${treatmentDetails[selectedTreatment].title}`}
              >
                <div className="treatment-image-overlay">
                  <div className="treatment-badge">
                    <FaTooth aria-hidden="true" /> Traitement
                  </div>
                </div>
                <img 
                  src={treatmentDetails[selectedTreatment].image} 
                  alt={treatmentDetails[selectedTreatment].title}
                  loading="lazy"
                  style={{ display: 'none' }}
                  onLoad={() => handleImageLoad(treatmentDetails[selectedTreatment].image)}
                  onError={(e) => {
                    e.target.parentElement.style.backgroundImage = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                  }}
                />
              </div>
            </div>
            
            {treatmentDetails[selectedTreatment].videoUrl && isValidVideoUrl(treatmentDetails[selectedTreatment].videoUrl) && !videoErrors[selectedTreatment] && (
              <motion.div 
                className="treatment-video-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="video-header">
                  <FaVideo className="video-icon" aria-hidden="true" />
                  <h3>Vidéo explicative</h3>
                </div>
                <div className="video-wrapper">
                  <iframe
                    src={treatmentDetails[selectedTreatment].videoUrl}
                    title={`Vidéo explicative - ${treatmentDetails[selectedTreatment].title}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="treatment-video"
                    loading="lazy"
                    onError={() => handleVideoError(selectedTreatment)}
                    aria-label={`Vidéo YouTube expliquant le traitement ${treatmentDetails[selectedTreatment].title}`}
                  />
                </div>
                <p className="video-description">
                  Regardez notre vidéo pour comprendre comment se déroule ce traitement
                </p>
              </motion.div>
            )}
            {treatmentDetails[selectedTreatment].videoUrl && (!isValidVideoUrl(treatmentDetails[selectedTreatment].videoUrl) || videoErrors[selectedTreatment]) && (
              <motion.div 
                className="treatment-video-container video-error"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="video-header">
                  <FaVideo className="video-icon" aria-hidden="true" />
                  <h3>Vidéo explicative</h3>
                </div>
                <div className="video-placeholder">
                  <div className="video-placeholder-content">
                    <FaVideo className="video-placeholder-icon" />
                    <h4>Vidéo non disponible</h4>
                    <p>La vidéo explicative sera bientôt disponible pour ce traitement.</p>
                    <p className="video-placeholder-note">
                      Pour plus d'informations, contactez-nous directement.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
          
          <div className="treatment-modal-info">
            <motion.div 
              className="treatment-header"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="treatment-icon">
                {services[selectedTreatment].icon}
              </div>
              <div>
                <h2 className="treatment-title">
                  {treatmentDetails[selectedTreatment].title}
                </h2>
                <p className="treatment-subtitle">
                  {treatmentDetails[selectedTreatment].description}
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              className="treatment-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="treatment-section">
                <h3>
                  <FaInfoCircle /> Description détaillée
                </h3>
                <p className="treatment-description">
                  {treatmentDetails[selectedTreatment].longDescription}
                </p>
              </div>
              
              <div className="treatment-section">
                <h3>
                  <FaCheckCircle /> Avantages principaux
                </h3>
                <div className="benefits-grid">
                  {treatmentDetails[selectedTreatment].benefits.map((benefit, index) => (
                    <motion.div 
                      key={index}
                      className="benefit-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      whileHover={{ x: 5 }}
                    >
                      <FaStar className="benefit-icon" />
                      <span>{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="treatment-meta">
                <motion.div 
                  className="meta-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <FaClock className="meta-icon" />
                  <div>
                    <h4>Durée</h4>
                    <p>{treatmentDetails[selectedTreatment].duration}</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div 
              className="treatment-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <button 
                className="btn-primary"
                onClick={() => {
                  setIsTreatmentModalOpen(false);
                  scrollToSection('contact');
                }}
                type="button"
                aria-label="Prendre rendez-vous pour ce traitement"
              >
                <FaCalendarCheck aria-hidden="true" /> Prendre rendez-vous
              </button>
              <button 
                className="btn-secondary"
                onClick={() => setIsTreatmentModalOpen(false)}
                type="button"
                aria-label="Fermer le modal et retourner aux traitements"
              >
                <FaArrowLeft aria-hidden="true" /> Retour aux traitements
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
    </motion.div>
  );
};

export default Home;